import { hasWebGpu } from "./modelBackend";
import "./styles.css";
import React from "react";
import { createRoot } from "react-dom/client";
import { AinautenFooter, AinautenHeader } from "@ainauten/ui";
import {
  applyRedactions,
  classFor,
  confidenceLabel,
  labelTitle,
  labelTitleEn,
  sortedActiveSpans,
} from "./redaction";
import type { PrivacyLabel, PrivacySpan, RedactionMode, WorkerMessage } from "./types";

type RunStatus = "idle" | "loading" | "processing" | "done" | "error";

const LABEL_ORDER: PrivacyLabel[] = [
  "private_person",
  "private_email",
  "private_phone",
  "private_address",
  "private_date",
  "private_url",
  "account_number",
  "secret",
];

const SAMPLE_TEXT = `Hallo AInauten,

mein Name ist Martina Keller. Sie erreichen mich unter martina.keller@example.com oder +49 176 12345678.
Meine Adresse ist Musterstraße 12, 8001 Zürich. Der Vertrag wurde am 2026-04-26 erstellt.
Account: CH9300762011623852957
Interner Secret: beispielwert

Bitte anonymisieren und danach manuell prüfen.`;

const POWER_USER_PROMPT = `Du bist mein lokaler Privacy-Filter-Assistent. Richte OpenAIs privacy-filter CLI nur lokal auf meinem Rechner ein, ohne Texte oder Dateien an einen eigenen Server zu senden.

Ziel:
- Installiere das offizielle Repo https://github.com/openai/privacy-filter in einem lokalen Arbeitsordner.
- Lege eine kurze README mit den lokalen Befehlen an.
- Verarbeite TXT/MD-Dateien mit opf -f.
- Weise mich darauf hin, dass PDF/DOCX zuerst lokal in Text umgewandelt werden müssen.
- Erkläre klar, dass das Ergebnis eine Redaktionshilfe ist und ich es manuell prüfen muss.

Vorgehen:
1. Prüfe Python, pip und freien Speicher.
2. Klone das Repo in einen lokalen Ordner.
3. Installiere es mit pip install -e .
4. Teste: opf --device cpu "Alice was born on 1990-01-02."
5. Erstelle einen lokalen Beispielordner input/ und output/.
6. Gib mir konkrete Befehle für Einzeldateien und Batch-Verarbeitung.`;

type Locale = "de" | "en";

const APP_COPY = {
  de: {
    statusOnline: "Online",
    gpuChecking: "WebGPU wird geprüft",
    gpuReady: "WebGPU bereit",
    gpuUnsupported: "CPU-Kompatibilitätsmodus",
    installInBrowser: "Im Browser installieren",
    watermarksTitle: "Watermarks entfernen",
    watermarksText: "Bald als neues AInauten-Tool unter watermarks.ainauten.com verfügbar.",
    watermarksCta: "Watermarks entfernen",
    noticeStrong: "Lokale Verarbeitung.",
    noticeText:
      "Text und Dateien bleiben im Browser. Ergebnisse sind eine Redaktionshilfe und müssen geprüft werden.",
    sourceLabel: "Quelle",
    sourceCollapse: "Quelle einklappen",
    sourceExpand: "Quelle ausklappen",
    sourceBody:
      "Ohne GPU: lokales mehrsprachiges PII-Modell im Kompatibilitätsmodus, mit abweichender Erkennung. Basierend auf OpenAIs Privacy Filter Source Code und dem Modell auf Hugging Face. Dieses AInauten-Tool ist kein offizieller OpenAI-Service.",
    outputLabel: "Ausgabe",
    outputModeCollapse: "Ausgabe einklappen",
    outputModeExpand: "Ausgabe ausklappen",
    mask: "Maskieren",
    dummyData: "Dummy-Daten",
    dataTypesLabel: "Datentypen",
    dataTypesCollapse: "Datentypen einklappen",
    dataTypesExpand: "Datentypen ausklappen",
    presetAll: "Alle",
    presetPeople: "Nur Personen",
    presetNumbers: "Nur Zahlen/IDs",
    presetNoNumbers: "Keine Zahlen",
    inputKicker: "Quelle",
    inputTitle: "Text oder Datei",
    inputCollapse: "Eingabe einklappen",
    inputExpand: "Eingabe ausklappen",
    sample: "Beispiel",
    dropZone: "Datei hier ablegen oder auswählen",
    dropZoneHint: "TXT, MD, PDF, DOCX - Ausgabe als bereinigter Text",
    placeholder: "Text einfügen oder Datei auswählen ...",
    anonymize: "Anonymisieren",
    clear: "Leeren",
    noSource: "Keine Quelle geladen",
    outputKicker: "Ergebnis",
    outputTitle: "Bereinigter Text",
    outputPanelCollapse: "Ergebnis einklappen",
    outputPanelExpand: "Ergebnis ausklappen",
    ready: "Bereit",
    modelNotLoaded: "Modell noch nicht geladen.",
    copy: "Kopieren",
    txtExport: ".txt Export",
    mdExport: ".md Export",
    reviewKicker: "Prüfung",
    reviewTitle: "Gefundene Stellen",
    reviewCollapse: "Gefundene Stellen einklappen",
    reviewExpand: "Gefundene Stellen ausklappen",
    noAnalysis: "Noch keine Analyse",
    noSpans: "Noch keine Stellen gefunden.",
    previewKicker: "Vorschau",
    previewTitle: "Markierungen im Kontext",
    previewCollapse: "Vorschau einklappen",
    previewExpand: "Vorschau ausklappen",
    noPreview: "Noch kein Text geladen.",
    batchPromptTitle: "Lokaler Batch-Prompt für Codex und andere Assistenten",
    copyPrompt: "Prompt kopieren",
    sources: "Quellen:",
    sourcesNote: "Nicht offizieller OpenAI-Service.",
    sourceDirect: "Direkte Eingabe",
    sourceExample: "Beispieltext",
    exampleNote: "Enthält synthetische Testdaten.",
    sourceNoteManual: "Bitte manuell prüfen.",
    markdownSource: "Quelle:",
    markdownHint: "Hinweis: Automatisch",
    markdownReview: "manuell prüfen.",
    emptySource: "Bitte zuerst Text einfügen oder eine Datei laden.",
    noWebGpu:
      "WebGPU fehlt. Für diese Browser-Version ist der lokale Modelllauf nicht verfügbar.",
    startingAnalysis: "Analyse startet lokal ...",
    fileLoaded: "Datei geladen. Starte jetzt die Verarbeitung.",
    fileReadError: "Datei konnte nicht gelesen werden.",
    dummyTitle: "Text mit Dummy-Daten",
    cleanedTitle: "Bereinigter Text",
    dummyAction: "Dummy-Daten einsetzen",
    anonymizeAction: "Anonymisieren",
    readyForAnalysis: "Bereit zur Analyse",
    modelSource: "Modell",
    patternSource: "Muster",
    dataTypeDisabled: "Datentyp deaktiviert",
    allMarkersDisabled: "Alle Markierungen sind deaktiviert.",
    noMarkers: "Noch keine Markierungen.",
    loading: "Lädt",
    processing: "Prüft",
    done: "Geprüft",
    error: "Hinweis",
    offline: "Offline",
    collapseLabel: (label: string) => `${label} einklappen`,
    foundNone: "Keine privaten Stellen erkannt. Bitte trotzdem manuell prüfen.",
    clearedOutput: "Modell noch nicht geladen.",
  },
  en: {
    statusOnline: "Online",
    gpuChecking: "Checking WebGPU",
    gpuReady: "WebGPU ready",
    gpuUnsupported: "CPU compatibility mode",
    installInBrowser: "Install in browser",
    watermarksTitle: "Remove watermarks",
    watermarksText: "Coming soon as a new AInauten tool at watermarks.ainauten.com.",
    watermarksCta: "Remove watermarks",
    noticeStrong: "Local processing.",
    noticeText:
      "Text and files stay in your browser. Results are a redaction aid and must be reviewed.",
    sourceLabel: "Source",
    sourceCollapse: "Collapse source",
    sourceExpand: "Expand source",
    sourceBody:
      "Without a GPU: a local multilingual PII model in compatibility mode, with different detection results. Based on OpenAI's Privacy Filter source code and the model on Hugging Face. This AInauten tool is not an official OpenAI service.",
    outputLabel: "Output",
    outputModeCollapse: "Collapse output",
    outputModeExpand: "Expand output",
    mask: "Mask",
    dummyData: "Dummy data",
    dataTypesLabel: "Data types",
    dataTypesCollapse: "Collapse data types",
    dataTypesExpand: "Expand data types",
    presetAll: "All",
    presetPeople: "People only",
    presetNumbers: "Numbers/IDs only",
    presetNoNumbers: "No numbers",
    inputKicker: "Source",
    inputTitle: "Text or file",
    inputCollapse: "Collapse input",
    inputExpand: "Expand input",
    sample: "Example",
    dropZone: "Drop a file here or choose one",
    dropZoneHint: "TXT, MD, PDF, DOCX - output as cleaned text",
    placeholder: "Paste text or choose a file ...",
    anonymize: "Anonymize",
    clear: "Clear",
    noSource: "No source loaded",
    outputKicker: "Result",
    outputTitle: "Cleaned text",
    outputPanelCollapse: "Collapse result",
    outputPanelExpand: "Expand result",
    ready: "Ready",
    modelNotLoaded: "Model not loaded yet.",
    copy: "Copy",
    txtExport: ".txt export",
    mdExport: ".md export",
    reviewKicker: "Review",
    reviewTitle: "Found items",
    reviewCollapse: "Collapse found items",
    reviewExpand: "Expand found items",
    noAnalysis: "No analysis yet",
    noSpans: "No items found yet.",
    previewKicker: "Preview",
    previewTitle: "Highlights in context",
    previewCollapse: "Collapse preview",
    previewExpand: "Expand preview",
    noPreview: "No text loaded yet.",
    batchPromptTitle: "Local batch prompt for Codex and other assistants",
    copyPrompt: "Copy prompt",
    sources: "Sources:",
    sourcesNote: "Not an official OpenAI service.",
    sourceDirect: "Direct input",
    sourceExample: "Example text",
    exampleNote: "Contains synthetic test data.",
    sourceNoteManual: "Please review manually.",
    markdownSource: "Source:",
    markdownHint: "Note: Automatically",
    markdownReview: "review manually.",
    emptySource: "Please paste text or load a file first.",
    noWebGpu:
      "WebGPU is missing. Local model inference is not available in this browser version.",
    startingAnalysis: "Starting local analysis ...",
    fileLoaded: "File loaded. Start processing now.",
    fileReadError: "The file could not be read.",
    dummyTitle: "Text with dummy data",
    cleanedTitle: "Cleaned text",
    dummyAction: "Insert dummy data",
    anonymizeAction: "Anonymize",
    readyForAnalysis: "Ready for analysis",
    modelSource: "Model",
    patternSource: "Pattern",
    dataTypeDisabled: "Data type disabled",
    allMarkersDisabled: "All highlights are disabled.",
    noMarkers: "No highlights yet.",
    loading: "Loading",
    processing: "Checking",
    done: "Checked",
    error: "Notice",
    offline: "Offline",
    collapseLabel: (label: string) => `Collapse ${label}`,
    foundNone: "No private items detected. Please still review manually.",
    clearedOutput: "Model not loaded yet.",
  },
} as const;

type CopyKey = keyof typeof APP_COPY.de;
const txt = (locale: Locale, key: CopyKey) => String(APP_COPY[locale][key]);

function resolveLocale(): Locale {
  const lang = new URLSearchParams(window.location.search).get("lang");
  return lang === "en" ? "en" : "de";
}

function appTemplate(locale: Locale): string {
  const t = txt.bind(null, locale);
  return `
  <div id="shellHeader"></div>

  <main class="workspace">
    <section class="status-strip" aria-label="${locale === "de" ? "Status und Installation" : "Status and installation"}">
      <span id="networkPill" class="status-pill">${t("statusOnline")}</span>
      <span id="gpuPill" class="status-pill status-pill--gpu">${t("gpuChecking")}</span>
      <button id="installButton" class="button button--ghost" type="button" hidden>${t("installInBrowser")}</button>
    </section>

    <section class="control-strip" aria-label="${locale === "de" ? "Verarbeitung konfigurieren" : "Configure processing"}">
      <div class="control-group control-group--output-mode">
        <p class="control-label">${t("outputLabel")}</p>
        <div class="segmented" role="radiogroup" aria-label="${t("outputLabel")}">
          <label><input type="radio" name="redactionMode" value="mask" checked /><span>${t("mask")}</span></label>
          <label><input type="radio" name="redactionMode" value="dummy" /><span>${t("dummyData")}</span></label>
        </div>
      </div>
      <div class="control-group control-group--wide collapsible" data-collapse-id="data-types">
        <div class="control-head control-group__head collapsible__trigger">
          <p class="control-label">${t("dataTypesLabel")}</p>
          <div class="control-head__actions">
            <div class="preset-row">
              <button class="preset-button" type="button" data-preset="all">${t("presetAll")}</button>
              <button class="preset-button" type="button" data-preset="people">${t("presetPeople")}</button>
              <button class="preset-button" type="button" data-preset="numbers">${t("presetNumbers")}</button>
              <button class="preset-button" type="button" data-preset="noNumbers">${t("presetNoNumbers")}</button>
            </div>
            <button class="collapse-toggle" type="button" aria-label="${t("dataTypesCollapse")}" aria-expanded="true"></button>
          </div>
        </div>
        <div class="collapsible__body">
          <div id="labelControls" class="label-controls"></div>
        </div>
      </div>
    </section>

    <section class="tool-grid">
      <div class="panel panel--input collapsible" data-collapse-id="input">
        <div class="panel__head collapsible__trigger">
          <div>
            <p class="panel__kicker">${t("inputKicker")}</p>
            <h2>${t("inputTitle")}</h2>
          </div>
          <div class="panel__actions">
            <button id="sampleButton" class="button button--soft" type="button">${t("sample")}</button>
            <button class="collapse-toggle" type="button" aria-label="${t("inputCollapse")}" aria-expanded="true"></button>
          </div>
        </div>

        <div class="collapsible__body">
          <label id="dropZone" class="drop-zone" for="fileInput">
            <span>${t("dropZone")}</span>
            <small>${t("dropZoneHint")}</small>
            <input id="fileInput" type="file" accept=".txt,.md,.pdf,.docx,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" />
          </label>

          <textarea id="sourceText" aria-describedby="localProcessingNote" spellcheck="false" placeholder="${t("placeholder")}"></textarea>

          <p id="localProcessingNote" class="local-processing-note"><strong>${t("noticeStrong")}</strong> ${t("noticeText")}</p>

          <div class="action-row">
            <button id="analyzeButton" class="button button--primary" type="button">${t("anonymize")}</button>
            <button id="clearButton" class="button button--ghost" type="button">${t("clear")}</button>
            <span id="sourceMeta" class="muted">${t("noSource")}</span>
          </div>
        </div>
      </div>

      <div class="panel panel--output collapsible" data-collapse-id="output">
        <div class="panel__head collapsible__trigger">
          <div>
            <p class="panel__kicker">${t("outputKicker")}</p>
            <h2 id="outputTitle">${t("outputTitle")}</h2>
          </div>
          <div class="panel__actions">
            <span id="runStatus" class="run-status">${t("ready")}</span>
            <button class="collapse-toggle" type="button" aria-label="${t("outputPanelCollapse")}" aria-expanded="true"></button>
          </div>
        </div>

        <div class="collapsible__body">
          <div class="progress">
            <div id="progressBar" class="progress__bar"></div>
          </div>
          <p id="progressText" class="progress__text">${t("modelNotLoaded")}</p>

          <textarea id="outputText" readonly spellcheck="false" placeholder="${t("noPreview")}"></textarea>

          <div class="action-row">
            <button id="copyButton" class="button button--primary" type="button" disabled>${t("copy")}</button>
            <button id="downloadTxtButton" class="button button--soft" type="button" disabled>${t("txtExport")}</button>
            <button id="downloadMdButton" class="button button--soft" type="button" disabled>${t("mdExport")}</button>
          </div>
        </div>
      </div>
    </section>

    <section class="review-layout">
      <div class="panel collapsible" data-collapse-id="findings">
        <div class="panel__head collapsible__trigger">
          <div>
            <p class="panel__kicker">${t("reviewKicker")}</p>
            <h2>${t("reviewTitle")}</h2>
          </div>
          <div class="panel__actions">
            <span id="spanSummary" class="muted">${t("noAnalysis")}</span>
            <button class="collapse-toggle" type="button" aria-label="${t("reviewCollapse")}" aria-expanded="true"></button>
          </div>
        </div>
        <div class="collapsible__body">
          <div id="spanList" class="span-list" aria-live="polite"></div>
        </div>
      </div>

      <div class="panel collapsible" data-collapse-id="preview">
        <div class="panel__head collapsible__trigger">
          <div>
            <p class="panel__kicker">${t("previewKicker")}</p>
            <h2>${t("previewTitle")}</h2>
          </div>
          <button class="collapse-toggle" type="button" aria-label="${t("previewCollapse")}" aria-expanded="true"></button>
        </div>
        <div class="collapsible__body">
          <div id="markedPreview" class="marked-preview">${t("noPreview")}</div>
        </div>
      </div>
    </section>

    <section class="reference-band">
      <details>
        <summary>${t("batchPromptTitle")}</summary>
        <div class="prompt-box">
          <pre id="powerPrompt"></pre>
          <button id="copyPromptButton" class="button button--soft" type="button">${t("copyPrompt")}</button>
        </div>
      </details>
      <details class="model-reference">
        <summary>${locale === "de" ? "Modelle, Quellen und Lizenzen" : "Models, sources and licenses"}</summary>
        <p>${t("sourceBody")}</p>
        <p><a href="https://github.com/openai/privacy-filter" target="_blank" rel="noreferrer">OpenAI Privacy Filter (Apache-2.0)</a> · <a href="https://huggingface.co/onnx-community/multilang-pii-ner-ONNX" target="_blank" rel="noreferrer">Multilang PII NER (MIT)</a></p>
      </details>
    </section>
  </main>

  <div id="shellFooter"></div>
`;
}

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("App root not found.");
}

let currentLocale: Locale = resolveLocale();
app.innerHTML = appTemplate(currentLocale);
document.documentElement.lang = currentLocale;

const shellHeaderRoot = query<HTMLDivElement>("#shellHeader");
const shellFooterRoot = query<HTMLDivElement>("#shellFooter");

function renderShell(locale: Locale) {
  if (!shellHeaderRoot || !shellFooterRoot) return;
  createRoot(shellHeaderRoot).render(
    React.createElement(AinautenHeader, {
      product: "privacy",
      activeApp: "PRIVACY",
      variant: "tool",
      locale,
      showThemeToggle: true,
      showLanguageToggle: true,
      onLanguageToggle: () => toggleLocale(),
      logoHref: "/",
      actions: React.createElement("a", {
        href: "https://watermarks.ainauten.com/",
        target: "_blank",
        rel: "noreferrer",
        className: "privacy-header-cta",
      }, txt(locale, "watermarksCta")),
    })
  );

  createRoot(shellFooterRoot).render(
    React.createElement(AinautenFooter, {
      product: "privacy",
      locale,
      variant: "full",
      showAppMap: true,
    })
  );
}

function toggleLocale(): void {
  const next: Locale = currentLocale === "de" ? "en" : "de";
  if (!app) return;
  const url = new URL(window.location.href);
  url.searchParams.set("lang", next);
  sessionStorage.setItem("ainauten-privacy:draft", sourceText.value);
  sessionStorage.setItem("ainauten-privacy:output", outputText.value);
  sessionStorage.setItem(
    "ainauten-privacy:progress",
    progressBar.style.width || ""
  );
  sessionStorage.setItem("ainauten-privacy:status", progressText.textContent ?? "");
  window.location.assign(url.toString());
}

if (shellHeaderRoot && shellFooterRoot) {
  renderShell(currentLocale);
}
const sourceText = query<HTMLTextAreaElement>("#sourceText");
const outputText = query<HTMLTextAreaElement>("#outputText");
const analyzeButton = query<HTMLButtonElement>("#analyzeButton");
const sampleButton = query<HTMLButtonElement>("#sampleButton");
const clearButton = query<HTMLButtonElement>("#clearButton");
const copyButton = query<HTMLButtonElement>("#copyButton");
const copyPromptButton = query<HTMLButtonElement>("#copyPromptButton");
const downloadTxtButton = query<HTMLButtonElement>("#downloadTxtButton");
const downloadMdButton = query<HTMLButtonElement>("#downloadMdButton");
const fileInput = query<HTMLInputElement>("#fileInput");
const dropZone = query<HTMLLabelElement>("#dropZone");
const sourceMeta = query<HTMLSpanElement>("#sourceMeta");
const runStatus = query<HTMLSpanElement>("#runStatus");
const progressBar = query<HTMLDivElement>("#progressBar");
const progressText = query<HTMLParagraphElement>("#progressText");
const spanList = query<HTMLDivElement>("#spanList");
const spanSummary = query<HTMLSpanElement>("#spanSummary");
const markedPreview = query<HTMLDivElement>("#markedPreview");
const gpuPill = query<HTMLSpanElement>("#gpuPill");
const networkPill = query<HTMLSpanElement>("#networkPill");
const installButton = query<HTMLButtonElement>("#installButton");
const powerPrompt = query<HTMLPreElement>("#powerPrompt");
const labelControls = query<HTMLDivElement>("#labelControls");
const outputTitle = query<HTMLHeadingElement>("#outputTitle");
const collapsibles = Array.from(document.querySelectorAll<HTMLElement>("[data-collapse-id]"));
const redactionModeInputs = Array.from(
  document.querySelectorAll<HTMLInputElement>('input[name="redactionMode"]')
);
const presetButtons = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-preset]"));

let status: RunStatus = "idle";
let spans: PrivacySpan[] = [];
let currentRequestId = "";
let currentSourceName = txt(currentLocale, "sourceDirect");
let extractionNote = "";
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let redactionMode: RedactionMode = "mask";
let activeLabels = createLabelSelection(true);

const worker = new Worker(new URL("./modelWorker.ts", import.meta.url), { type: "module" });
let webGpuSupported: boolean | null = null;
let backendSelected = false;
void hasWebGpu(navigator as Parameters<typeof hasWebGpu>[0]).then(available => {
  if (backendSelected) return;
  webGpuSupported = available;
  updateGpuPill();
});

powerPrompt.textContent = POWER_USER_PROMPT;
sourceText.value = "";
outputText.value = "";
renderLabelControls();
initializeCollapsibles();

worker.addEventListener("message", (event: MessageEvent<WorkerMessage>) => {
  if (event.data.type === "backend") {
    backendSelected = true;
    webGpuSupported = event.data.backend === "webgpu";
    updateGpuPill();
    return;
  }
  if (event.data.requestId !== currentRequestId) return;

  if (event.data.type === "progress") {
    status = "loading";
    progressText.textContent = event.data.message;
    setProgress(event.data.progress ?? 12);
    setStatus();
    return;
  }

  if (event.data.type === "result") {
    status = "done";
    spans = event.data.spans;
    progressText.textContent = spans.length
      ? foundCountLabel(currentLocale, spans.length)
      : txt(currentLocale, "foundNone");
    setProgress(100);
    refreshOutput();
    setStatus();
    return;
  }

  status = "error";
  progressText.textContent = event.data.message;
  setProgress(0);
  setStatus();
});

sourceText.addEventListener("input", () => {
  spans = [];
  currentSourceName = txt(currentLocale, "sourceDirect");
  extractionNote = "";
  status = "idle";
  refreshOutput();
  setStatus();
});

sampleButton.addEventListener("click", () => {
  sourceText.value = SAMPLE_TEXT;
  spans = [];
  currentSourceName = txt(currentLocale, "sourceExample");
  extractionNote = txt(currentLocale, "exampleNote");
  status = "idle";
  refreshOutput();
  setStatus();
});

clearButton.addEventListener("click", () => {
  sourceText.value = "";
  outputText.value = "";
  spans = [];
  currentSourceName = txt(currentLocale, "sourceDirect");
  extractionNote = "";
  status = "idle";
  setProgress(0);
  progressText.textContent = txt(currentLocale, "clearedOutput");
  refreshOutput();
  setStatus();
});

analyzeButton.addEventListener("click", () => {
  void analyzeCurrentText();
});

copyButton.addEventListener("click", () => {
  void copyToClipboard(outputText.value, copyButton, txt(currentLocale, "copy"));
});

copyPromptButton.addEventListener("click", () => {
  void copyToClipboard(POWER_USER_PROMPT, copyPromptButton, txt(currentLocale, "copyPrompt"));
});

downloadTxtButton.addEventListener("click", () => {
  downloadText(outputText.value, "ainauten-privacy-filter-output.txt", "text/plain");
});

downloadMdButton.addEventListener("click", () => {
  const method =
    redactionMode === "dummy"
      ? currentLocale === "de"
        ? "durch Dummy-Daten ersetzt"
        : "replaced with dummy data"
      : currentLocale === "de"
        ? "maskiert"
        : "masked";
  const markdown = `# AInauten Privacy Filter Output

${txt(currentLocale, "markdownSource")} ${currentSourceName}
${txt(currentLocale, "markdownHint")} ${method}, ${txt(currentLocale, "markdownReview")}

\`\`\`text
${outputText.value}
\`\`\`
`;
  downloadText(markdown, "ainauten-privacy-filter-output.md", "text/markdown");
});

fileInput.addEventListener("change", () => {
  const file = fileInput.files?.[0];
  if (file) {
    void loadFile(file);
  }
});

dropZone.addEventListener("dragover", (event) => {
  event.preventDefault();
  dropZone.classList.add("drop-zone--active");
});

dropZone.addEventListener("dragleave", () => {
  dropZone.classList.remove("drop-zone--active");
});

dropZone.addEventListener("drop", (event) => {
  event.preventDefault();
  dropZone.classList.remove("drop-zone--active");
  const file = event.dataTransfer?.files[0];
  if (file) {
    void loadFile(file);
  }
});

spanList.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  const index = Number(target.dataset.index);
  if (!Number.isInteger(index) || !spans[index]) return;

  spans[index] = {
    ...spans[index],
    enabled: target.checked,
  };
  refreshOutput();
});

labelControls.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  const label = target.dataset.label as PrivacyLabel | undefined;
  if (!label || !LABEL_ORDER.includes(label)) return;

  activeLabels = {
    ...activeLabels,
    [label]: target.checked,
  };
  refreshOutput();
});

for (const input of redactionModeInputs) {
  input.addEventListener("change", () => {
    if (!input.checked) return;
    redactionMode = input.value === "dummy" ? "dummy" : "mask";
    refreshOutput();
  });
}

for (const button of presetButtons) {
  button.addEventListener("click", () => {
    applyPreset(button.dataset.preset ?? "all");
  });
}

window.addEventListener("online", updateNetworkPill);
window.addEventListener("offline", updateNetworkPill);

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferredInstallPrompt = event as BeforeInstallPromptEvent;
  installButton.hidden = false;
});

installButton.addEventListener("click", async () => {
  if (!deferredInstallPrompt) return;
  await deferredInstallPrompt.prompt();
  deferredInstallPrompt = null;
  installButton.hidden = true;
});

if ("serviceWorker" in navigator) {
  const hadServiceWorkerController = Boolean(navigator.serviceWorker.controller);
  let hasReloadedForServiceWorkerUpdate = false;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadServiceWorkerController || hasReloadedForServiceWorkerUpdate) return;
    hasReloadedForServiceWorkerUpdate = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => registration.update())
      .catch(() => undefined);
  });
}

updateNetworkPill();
updateGpuPill();
restoreSessionState();
refreshOutput();
setStatus();

function restoreSessionState(): void {
  const savedDraft = sessionStorage.getItem("ainauten-privacy:draft");
  const savedOutput = sessionStorage.getItem("ainauten-privacy:output");
  const savedProgress = sessionStorage.getItem("ainauten-privacy:progress");
  const savedStatus = sessionStorage.getItem("ainauten-privacy:status");

  if (savedDraft) sourceText.value = savedDraft;
  if (savedOutput) outputText.value = savedOutput;
  if (savedProgress) setProgress(Number(savedProgress.replace("%", "")) || 0);
  if (savedStatus) progressText.textContent = savedStatus;

  sessionStorage.removeItem("ainauten-privacy:draft");
  sessionStorage.removeItem("ainauten-privacy:output");
  sessionStorage.removeItem("ainauten-privacy:progress");
  sessionStorage.removeItem("ainauten-privacy:status");
}

async function analyzeCurrentText(): Promise<void> {
  const text = sourceText.value.trim();
  if (!text) {
    progressText.textContent = txt(currentLocale, "emptySource");
    return;
  }

  status = "processing";
  spans = [];
  currentRequestId = crypto.randomUUID();
  progressText.textContent = txt(currentLocale, "startingAnalysis");
  setProgress(8);
  setStatus();
  refreshOutput();
  worker.postMessage({ type: "analyze", requestId: currentRequestId, text, locale: currentLocale });
}

async function loadFile(file: File): Promise<void> {
  try {
    status = "loading";
    progressText.textContent = readingFileLabel(currentLocale, file.name);
    setProgress(6);
    setStatus();

    const { extractTextFromFile } = await import("./fileExtractors");
    const extracted = await extractTextFromFile(file);
    sourceText.value = extracted.text;
    spans = [];
    currentSourceName = file.name;
    extractionNote = extracted.note;
    status = "idle";
    setProgress(0);
    progressText.textContent = txt(currentLocale, "fileLoaded");
    refreshOutput();
    setStatus();
  } catch (error) {
    status = "error";
    progressText.textContent =
      error instanceof Error ? error.message : txt(currentLocale, "fileReadError");
    setProgress(0);
    setStatus();
  } finally {
    fileInput.value = "";
  }
}

function refreshOutput(): void {
  const text = sourceText.value;
  const effectiveSpans = getEffectiveSpans();
  const redacted = applyRedactions(text, effectiveSpans, redactionMode);
  outputText.value = redacted === text && spans.length === 0 ? "" : redacted;
  outputTitle.textContent =
    redactionMode === "dummy" ? txt(currentLocale, "dummyTitle") : txt(currentLocale, "cleanedTitle");
  analyzeButton.textContent =
    redactionMode === "dummy" ? txt(currentLocale, "dummyAction") : txt(currentLocale, "anonymizeAction");

  const hasOutput = outputText.value.trim().length > 0;
  copyButton.disabled = !hasOutput;
  downloadTxtButton.disabled = !hasOutput;
  downloadMdButton.disabled = !hasOutput;

  renderSpanList();
  renderPreview();
  updateSourceMeta();
}

function renderSpanList(): void {
  if (!spans.length) {
    spanList.innerHTML = `<p class="empty-state">${txt(currentLocale, "noSpans")}</p>`;
    spanSummary.textContent = sourceText.value.trim()
      ? txt(currentLocale, "readyForAnalysis")
      : txt(currentLocale, "noAnalysis");
    return;
  }

  const enabledCount = getEffectiveSpans().length;
  spanSummary.textContent = activeCountLabel(currentLocale, enabledCount, spans.length);
  spanList.innerHTML = "";

  spans.forEach((span, index) => {
    const row = document.createElement("label");
    row.className = `span-row span-row--${classFor(span.label)}`;
    if (!activeLabels[span.label]) {
      row.classList.add("span-row--inactive");
    }

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = span.enabled;
    checkbox.dataset.index = String(index);
    checkbox.disabled = !activeLabels[span.label];

    const content = document.createElement("span");
    content.className = "span-row__content";

    const header = document.createElement("span");
    header.className = "span-row__header";

    const tag = document.createElement("span");
    tag.className = "span-tag";
    tag.textContent = labelTitleFor(span.label);

    const meta = document.createElement("span");
    meta.className = "span-row__meta";
    meta.textContent = activeLabels[span.label]
      ? `${confidenceLabel(span.score)} - ${
          span.source === "model"
            ? txt(currentLocale, "modelSource")
            : txt(currentLocale, "patternSource")
        }`
      : txt(currentLocale, "dataTypeDisabled");

    const value = document.createElement("span");
    value.className = "span-row__value";
    value.textContent = span.text;

    header.append(tag, meta);
    content.append(header, value);
    row.append(checkbox, content);
    spanList.append(row);
  });
}

function renderPreview(): void {
  const text = sourceText.value;
  const active = sortedActiveSpans(getEffectiveSpans());

  markedPreview.innerHTML = "";

  if (!text.trim()) {
    markedPreview.textContent = txt(currentLocale, "noPreview");
    return;
  }

  if (!active.length) {
    markedPreview.textContent = spans.length
      ? txt(currentLocale, "allMarkersDisabled")
      : txt(currentLocale, "noMarkers");
    return;
  }

  let cursor = 0;
  for (const span of active) {
    if (span.start > cursor) {
      markedPreview.append(document.createTextNode(text.slice(cursor, span.start)));
    }

    const mark = document.createElement("mark");
    mark.className = `entity entity--${classFor(span.label)}`;
    mark.title = labelTitleFor(span.label);
    mark.textContent = text.slice(span.start, span.end);
    markedPreview.append(mark);
    cursor = span.end;
  }

  if (cursor < text.length) {
    markedPreview.append(document.createTextNode(text.slice(cursor)));
  }
}

function renderLabelControls(): void {
  labelControls.innerHTML = "";

  for (const label of LABEL_ORDER) {
    const item = document.createElement("label");
    item.className = `label-chip label-chip--${classFor(label)}`;

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = activeLabels[label];
    checkbox.dataset.label = label;

    const text = document.createElement("span");
    text.textContent = labelTitleFor(label);

    item.append(checkbox, text);
    labelControls.append(item);
  }
}

function labelTitleFor(label: PrivacyLabel): string {
  return currentLocale === "en" ? labelTitleEn(label) : labelTitle(label);
}

function foundCountLabel(locale: Locale, count: number): string {
  return locale === "de"
    ? `${count} Stelle${count === 1 ? "" : "n"} gefunden. Bitte prüfen.`
    : `${count} item${count === 1 ? "" : "s"} found. Please review.`;
}

function readingFileLabel(locale: Locale, name: string): string {
  return locale === "de"
    ? `${name} wird lokal gelesen ...`
    : `Reading ${name} locally ...`;
}

function activeCountLabel(locale: Locale, enabled: number, total: number): string {
  return locale === "de"
    ? `${enabled} von ${total} aktiv`
    : `${enabled} of ${total} active`;
}

function applyPreset(preset: string): void {
  const next = createLabelSelection(false);

  if (preset === "people") {
    for (const label of ["private_person", "private_email", "private_phone", "private_address"]) {
      next[label as PrivacyLabel] = true;
    }
  } else if (preset === "numbers") {
    for (const label of ["private_phone", "private_date", "account_number", "secret"]) {
      next[label as PrivacyLabel] = true;
    }
  } else if (preset === "noNumbers") {
    for (const label of ["private_person", "private_email", "private_address", "private_url", "secret"]) {
      next[label as PrivacyLabel] = true;
    }
  } else {
    for (const label of LABEL_ORDER) {
      next[label] = true;
    }
  }

  activeLabels = next;
  renderLabelControls();
  refreshOutput();
}

function getEffectiveSpans(): PrivacySpan[] {
  return spans.filter((span) => span.enabled && activeLabels[span.label]);
}

function createLabelSelection(value: boolean): Record<PrivacyLabel, boolean> {
  return LABEL_ORDER.reduce(
    (selection, label) => {
      selection[label] = value;
      return selection;
    },
    {} as Record<PrivacyLabel, boolean>
  );
}

function setStatus(): void {
  const statusLabels: Record<RunStatus, string> = {
    idle: txt(currentLocale, "ready"),
    loading: txt(currentLocale, "loading"),
    processing: txt(currentLocale, "processing"),
    done: txt(currentLocale, "done"),
    error: txt(currentLocale, "error"),
  };

  runStatus.textContent = statusLabels[status];
  runStatus.dataset.status = status;
  analyzeButton.disabled = status === "loading" || status === "processing" || !sourceText.value.trim();
}

function setProgress(value: number): void {
  const bounded = Math.max(0, Math.min(100, value));
  progressBar.style.width = `${bounded}%`;
}

function updateSourceMeta(): void {
  const length = sourceText.value.length;
  const words = sourceText.value.trim() ? sourceText.value.trim().split(/\s+/).length : 0;
  const note = extractionNote ? ` - ${extractionNote}` : "";
  const wordsLabel = currentLocale === "de"
    ? `${words} Wörter`
    : `${words} word${words === 1 ? "" : "s"}`;
  sourceMeta.textContent = length
    ? `${currentSourceName} - ${wordsLabel}${note}`
    : txt(currentLocale, "noSource");
}

function updateGpuPill(): void {
  gpuPill.textContent = webGpuSupported === null ? txt(currentLocale, "gpuChecking") : webGpuSupported ? txt(currentLocale, "gpuReady") : txt(currentLocale, "gpuUnsupported");
  gpuPill.dataset.state = webGpuSupported ? "ok" : "warn";
  gpuPill.title = currentLocale === "de"
    ? "Ohne GPU wird ein anderes lokales Erkennungsmodell verwendet. Die Erkennung kann abweichen. Ergebnisse immer prüfen."
    : "Without a GPU, a different local detection model is used. Detection may differ. Always review results.";
}

function updateNetworkPill(): void {
  networkPill.textContent = navigator.onLine
    ? txt(currentLocale, "statusOnline")
    : txt(currentLocale, "offline");
  networkPill.dataset.state = navigator.onLine ? "ok" : "warn";
}

function initializeCollapsibles(): void {
  for (const collapsible of collapsibles) {
    const id = collapsible.dataset.collapseId;
    const button = collapsible.querySelector<HTMLButtonElement>(".collapse-toggle");
    const body = collapsible.querySelector<HTMLElement>(".collapsible__body");
    const triggers = Array.from(collapsible.querySelectorAll<HTMLElement>(".collapsible__trigger"));
    if (!id || !button || !body) continue;

    const storedState = localStorage.getItem(collapseStorageKey(id));
    const isCollapsed = storedState === "collapsed";
    setCollapsedState(collapsible, button, body, isCollapsed);

    const toggle = () => {
      const nextState = !collapsible.classList.contains("is-collapsed");
      setCollapsedState(collapsible, button, body, nextState);
      localStorage.setItem(collapseStorageKey(id), nextState ? "collapsed" : "expanded");
    };

    button.addEventListener("click", (event) => {
      event.stopPropagation();
      toggle();
    });

    for (const trigger of triggers) {
      trigger.addEventListener("click", (event) => {
        if (isInteractiveClick(event.target, button)) return;
        toggle();
      });
    }
  }
}

function setCollapsedState(
  collapsible: HTMLElement,
  button: HTMLButtonElement,
  body: HTMLElement,
  isCollapsed: boolean
): void {
  collapsible.classList.toggle("is-collapsed", isCollapsed);
  body.hidden = isCollapsed;
  button.setAttribute("aria-expanded", String(!isCollapsed));
  const baseLabel = collapseLabel(button);
  const action = isCollapsed
    ? currentLocale === "de"
      ? "ausklappen"
      : "expand"
    : currentLocale === "de"
      ? "einklappen"
      : "collapse";
  button.setAttribute("aria-label", `${baseLabel} ${action}`);
}

function isInteractiveClick(target: EventTarget | null, collapseButton: HTMLButtonElement): boolean {
  if (!(target instanceof Element)) return false;
  const interactive = target.closest("a, button, input, label, select, textarea");
  return Boolean(interactive && interactive !== collapseButton);
}

function collapseLabel(button: HTMLButtonElement): string {
  const label = button.getAttribute("aria-label") ?? "";
  return label
    .replace(/\s+(ein|aus)klappen$/i, "")
    .replace(/\s+(expand|collapse)$/i, "");
}

function collapseStorageKey(id: string): string {
  return `ainauten-privacy-collapse:${id}`;
}

async function copyToClipboard(text: string, button: HTMLButtonElement, successLabel: string): Promise<void> {
  const original = button.textContent ?? "";
  await navigator.clipboard.writeText(text);
  button.textContent = successLabel;
  window.setTimeout(() => {
    button.textContent = original;
  }, 1400);
}

function downloadText(text: string, filename: string, type: string): void {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function query<T extends HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Element not found: ${selector}`);
  }
  return element;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}
